import cn from 'classnames'
import { Controller, useForm } from 'react-hook-form'

import styles from './FeedbackPostCommentsInput.module.scss'

import Button from '~elements/Button'
import Checkbox from '~elements/Checkbox/Old'
import Box from '~lib/ui-kit/Box'
import Text from '~lib/ui-kit/Text'
import Textarea from '~lib/ui-kit/Textarea'
import useCreateFeedbackPostComment from '~modules/feedbackTool/usecases/useCreateFeedbackPostComment'
import { ReplyToOptions } from '~modules/feedbackTool/utils'

interface FeedbackPostCommentsInputProps {
  feedbackPostId: string
  replyingTo: ReplyToOptions
  onResetReplyingTo: () => void
  className?: string
  disabled?: boolean
}

function FeedbackPostCommentsInput({
  feedbackPostId,
  replyingTo,
  onResetReplyingTo,
  className,
  disabled,
}: FeedbackPostCommentsInputProps) {
  const { handleSubmit, reset, control, formState } = useForm({
    defaultValues: {
      text: '',
      internal: true,
      notifySubscribers: false,
    },
  })

  const [createFeedbackComment, { loading }] = useCreateFeedbackPostComment({
    onCompleted: () => {
      reset()
      onResetReplyingTo()
    },
  })

  const onSubmit = handleSubmit(({ text, notifySubscribers, internal }: Record<string, any>) => {
    createFeedbackComment({
      postId: feedbackPostId,
      text,
      notifySubscribers,
      internal,
      replyTo: replyingTo?.commentId,
    })
  })

  return (
    <Box as="form" onSubmit={onSubmit} className={cn(styles.root, className)}>
      {replyingTo?.commentId && (
        <Text as="button" type="body-s" variant="primary" className="mb-1" onClick={onResetReplyingTo}>
          Replying to {replyingTo?.authorName}
        </Text>
      )}

      <Controller
        name="text"
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Textarea
            disabled={disabled}
            placeholder="Add a comment"
            name={name}
            value={value}
            minRows={4}
            maxRows={7}
            onChange={onChange}
            className="mb-2"
          />
        )}
      />

      <div className={styles.controls}>
        <Controller
          name="notifySubscribers"
          control={control}
          render={({ field: { value, name, onChange } }) => (
            <Checkbox
              disabled={disabled}
              label="Notify subscribers"
              name={name}
              checked={value}
              onClick={onChange}
              className="mr-4"
            />
          )}
        />

        <Controller
          name="internal"
          control={control}
          render={({ field: { name, value, onChange } }) => {
            return <Checkbox name={name} checked={value} onClick={onChange} label="Internal" disabled={disabled} />
          }}
        />

        <Button
          loading={loading}
          disabled={loading || !formState.dirtyFields.text || disabled}
          variant="lavender"
          size="l"
          type="submit"
          className={styles.submit}
        >
          Add comment
        </Button>
      </div>
    </Box>
  )
}

export default FeedbackPostCommentsInput
